<template>
  <div>
    <div class="tile">
      <div class="form-inline">
        <b-form-input type="month" v-model="filterFormModel.period" class="mr-1" @change="load"></b-form-input>
        <admin-user-select v-if="$store.state.user.role !== 'sales'" :model="filterFormModel" attribute="sales" role-filter="sales" @change="load" :place-holder="$t('selectSales')" class="mr-1"></admin-user-select>
        <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
        <button @click="reset" class="btn btn-secondary mr-1">Reset</button>
        <button @click="exportExcel" class="btn btn-primary mr-1" :disabled="!items.length && !details.length">
          <i class="fa fa-download"></i> {{ $t('exportExcel') }}
        </button>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <caption class="text-center text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('reportTitle') }} {{ monthLabel }}
          </caption>
          <thead>
            <tr>
              <th rowspan="2" class="align-middle">{{ $t('stt') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('fullName') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('region') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('position') }}</th>
              <th colspan="2" class="align-middle">{{ $t('kpi') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('revenueBase') }}</th>
              <th colspan="5" class="align-middle">{{ $t('commission') }}</th>
            </tr>
            <tr>
              <th class="align-middle">{{ $t('kpiPercent') }}</th>
              <th class="align-middle">{{ $t('kpiBracket') }}</th>
              <th class="align-middle">{{ $t('commissionN') }}</th>
              <th class="align-middle">{{ $t('commissionNStar') }}</th>
              <th class="align-middle">{{ $t('commissionEAU') }}</th>
              <th class="align-middle">{{ $t('commissionCallFee') }}</th>
              <th class="text-danger align-middle">{{ $t('total') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="region in regions">
              <tr v-for="(item, index) in region.employees" :key="item.userId">
                <td class="align-middle">{{ region.startIndex + index + 1 }}</td>
                <td class="text-left align-middle">{{ item.name }}</td>
                <td v-if="index === 0" :rowspan="region.employees.length" class="align-middle font-weight-bold">
                  {{ region.office }}
                </td>
                <td class="align-middle">{{ position(item.role) }}</td>
                <td class="align-middle">{{ percent(item.kpiPercent) }}</td>
                <td class="align-middle">{{ item.kpiBracket }}</td>
                <td class="text-right align-middle">{{ item.revenueBase | numFormat }}</td>
                <td class="text-right align-middle">{{ item.commissionN | numFormat }}</td>
                <td class="text-right align-middle">{{ item.commissionNStar | numFormat }}</td>
                <td class="text-right align-middle">{{ item.commissionEAU | numFormat }}</td>
                <td class="text-right align-middle">{{ item.commissionCallFee | numFormat }}</td>
                <td class="text-right font-weight-bold text-danger align-middle">
                  <span v-if="item.total > 0">{{ item.total | numFormat }}</span>
                  <span v-else>-</span>
                </td>
              </tr>
            </template>
            <tr v-if="items.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="4" class="text-left align-middle">{{ $t('grandTotal') }}</td>
              <td></td>
              <td></td>
              <td class="text-right align-middle">{{ mainTotal.revenueBase | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionN | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionNStar | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionEAU | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionCallFee | numFormat }}</td>
              <td class="text-right text-danger align-middle">{{ mainTotal.total | numFormat }}</td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="12" class="text-danger">{{ $t('noData') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tile">
      <h5 class="text-danger font-weight-bold mb-3">{{ $t('policyTitle') }}</h5>
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <thead>
            <tr>
              <th class="align-middle">{{ $t('kpi') }}</th>
              <th class="align-middle">CallioN ({{ $t('policySourceSelf') }})</th>
              <th class="align-middle">CallioN* ({{ $t('policySourceCompany') }})</th>
              <th class="align-middle">CallioE, CallioA, CallioU</th>
              <th class="align-middle">{{ $t('policyCallFee') }}</th>
              <th class="align-middle">{{ $t('policyBig') }}</th>
              <th class="align-middle">{{ $t('policyBigCallFee') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in policy" :key="row.kpi">
              <td class="font-weight-bold align-middle">{{ row.kpi }}</td>
              <td class="align-middle">{{ row.n }}</td>
              <td class="align-middle">{{ row.nStar }}</td>
              <td class="align-middle">{{ row.eau }}</td>
              <td class="align-middle">{{ row.callFee }}</td>
              <td class="align-middle">{{ row.big }}</td>
              <td class="align-middle">{{ row.bigCallFee }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center commission-detail-table">
          <caption class="text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('detailTitle') }} {{ monthLabel }}
          </caption>
          <thead>
            <tr>
              <th class="align-middle">{{ $t('stt') }}</th>
              <th class="align-middle">{{ $t('dSales') }}</th>
              <th class="align-middle">{{ $t('dClientName') }}</th>
              <th class="align-middle">{{ $t('dDomain') }}</th>
              <th class="align-middle">{{ $t('dSource') }}</th>
              <th class="align-middle">{{ $t('dRevenueType') }}</th>
              <th class="align-middle">{{ $t('dPlanName') }}</th>
              <th class="align-middle">{{ $t('dUserPrice') }}</th>
              <th class="align-middle">{{ $t('dUserLimit') }}</th>
              <th class="align-middle">{{ $t('dExtraUser') }}</th>
              <th class="align-middle">{{ $t('dMonthCount') }}</th>
              <th class="align-middle">{{ $t('dConfirmTime') }}</th>
              <th class="align-middle">{{ $t('dOrderValue') }}</th>
              <th class="align-middle">{{ $t('dAgencyPay') }}</th>
              <th class="align-middle">{{ $t('dRevenueBase') }}</th>
              <th class="align-middle">{{ $t('dKpiBracket') }}</th>
              <th class="align-middle">{{ $t('dCommissionPercent') }}</th>
              <th class="align-middle">{{ $t('dCommission') }}</th>
              <th class="align-middle">{{ $t('dNote') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in details" :key="index"
              :style="row.isReExtend ? 'background-color: #dff0d8;' : row.isBig ? 'background-color: #f2dede;' : ''">
              <td class="align-middle">{{ index + 1 }}</td>
              <td class="text-left align-middle">{{ row.sales }}</td>
              <td class="text-left align-middle">{{ row.companyName }}</td>
              <td class="text-left align-middle">{{ row.domain }}</td>
              <td class="text-left align-middle">{{ row.source }}</td>
              <td class="text-left align-middle">{{ row.loai }}</td>
              <td class="text-left align-middle">{{ row.planName }}</td>
              <td class="text-right align-middle">{{ row.userPrice | numFormat }}</td>
              <td class="text-right align-middle">{{ row.userLimit | numFormat }}</td>
              <td class="text-right align-middle">{{ row.numberOfUserToBuy | numFormat }}</td>
              <td class="text-right align-middle">{{ row.monthCount }}</td>
              <td class="align-middle">{{ fmtTime(row.confirmTime) }}</td>
              <td class="text-right align-middle">{{ row.orderValue | numFormat }}</td>
              <td class="text-right align-middle">
                <span v-if="row.agencyCommission > 0">{{ row.agencyCommission | numFormat }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-right align-middle">{{ row.revenueBase | numFormat }}</td>
              <td class="align-middle">{{ row.kpiBracket }}</td>
              <td class="text-right align-middle">{{ percent(row.commissionPercent) }}</td>
              <td class="text-right font-weight-bold align-middle">{{ row.commission | numFormat }}</td>
              <td class="text-left align-middle">{{ row.note }}</td>
            </tr>
            <tr v-if="!details.length">
              <td colspan="19" class="text-danger">{{ $t('noDetail') }}</td>
            </tr>
            <tr v-if="details.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="12" class="text-left align-middle">{{ $t('grandTotal') }}</td>
              <td class="text-right align-middle">{{ detailTotal.orderValue | numFormat }}</td>
              <td class="text-right align-middle">{{ detailTotal.agencyCommission | numFormat }}</td>
              <td class="text-right align-middle">{{ detailTotal.revenueBase | numFormat }}</td>
              <td></td>
              <td></td>
              <td class="text-right text-danger align-middle">{{ detailTotal.commission | numFormat }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import AdminUserSelect from '@/components/AdminUserSelect';

export default {
  components: { AdminUserSelect },
  data() {
    return {
      items: [],
      details: [],
      filterFormModel: { period: moment().format('YYYY-MM'), sales: null },
      // Bảng chính sách hoa hồng (tĩnh) - khớp với revenueCommissionBracket / callFeeCommissionBracket ở backend
      policy: [
        { kpi: '60-100%', n: '6.00%', nStar: '3.00%', eau: '3.00%', callFee: '0.80%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: '100-120%', n: '12.00%', nStar: '4.00%', eau: '4.00%', callFee: '0.90%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: 'KPI>120%', n: '13.00%', nStar: '5.00%', eau: '5.00%', callFee: '1.00%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: 'KPI>150%', n: '16.00%', nStar: '6.00%', eau: '6.00%', callFee: '1.00%', big: '1.00%', bigCallFee: '0.50%' },
      ],
    }
  },
  computed: {
    monthLabel() {
      return moment(this.filterFormModel.period, 'YYYY-MM').format('MM/YYYY');
    },
    regions() {
      // Gom theo khu vực (Phòng), trong khu vực sắp xếp theo tổng hoa hồng giảm dần
      let map = {};
      let order = [];
      this.items.forEach(item => {
        let office = item.office || this.$t('unknownRegion');
        if (!map[office]) {
          map[office] = { office, employees: [] };
          order.push(office);
        }
        map[office].employees.push(item);
      });
      order.forEach(office => {
        map[office].employees.sort((a, b) => b.total - a.total);
      });
      let regions = order.map(office => map[office]).sort((a, b) => a.office.localeCompare(b.office));
      let startIndex = 0;
      regions.forEach(region => {
        region.startIndex = startIndex;
        startIndex += region.employees.length;
      });
      return regions;
    },
    mainTotal() {
      return this.items.reduce((acc, item) => {
        acc.revenueBase += item.revenueBase || 0;
        acc.commissionN += item.commissionN || 0;
        acc.commissionNStar += item.commissionNStar || 0;
        acc.commissionEAU += item.commissionEAU || 0;
        acc.commissionCallFee += item.commissionCallFee || 0;
        acc.total += item.total || 0;
        return acc;
      }, { revenueBase: 0, commissionN: 0, commissionNStar: 0, commissionEAU: 0, commissionCallFee: 0, total: 0 });
    },
    detailTotal() {
      return this.details.reduce((acc, row) => {
        acc.orderValue += row.orderValue || 0;
        acc.agencyCommission += row.agencyCommission || 0;
        acc.revenueBase += row.revenueBase || 0;
        acc.commission += row.commission || 0;
        return acc;
      }, { orderValue: 0, agencyCommission: 0, revenueBase: 0, commission: 0 });
    },
  },
  methods: {
    load() {
      let [year, month] = this.filterFormModel.period.split('-');
      let params = { month: parseInt(month), year: parseInt(year) };
      if (this.filterFormModel.sales) {
        params.sales = this.filterFormModel.sales;
      }
      let loader = this.$loading.show();
      Promise.all([
        this.$service.get('/report/commission', { params }),
        this.$service.get('/report/commission-detail', { params }),
      ]).then(([commission, detail]) => {
        this.items = commission.data;
        this.details = detail.data;
      }).finally(() => {
        loader.hide();
      });
    },
    reset() {
      this.filterFormModel.period = moment().format('YYYY-MM');
      this.filterFormModel.sales = null;
      this.load();
    },
    fmtTime(t) {
      return t ? moment(t).format('DD/MM/YYYY HH:mm:ss') : '';
    },
    percent(value) {
      return (value || 0).toFixed(2) + '%';
    },
    position(role) {
      const map = {
        supervisor: this.$t('roleSupervisor'),
        manager: this.$t('roleManager'),
      };
      return map[role] || this.$t('roleConsultant');
    },
    // Tô nền nguyên dòng (cả ô trống) trong phạm vi `count` cột - giống màu nền các bảng ở FE
    xlsxFillRow(row, argb, count) {
      for (let c = 1; c <= count; c++) {
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    },
    // Kẻ viền + canh giữa cho toàn bộ vùng chữ nhật [r1..r2] x [c1..c2] (gồm cả ô trống)
    xlsxBorderRange(sheet, r1, r2, c1, c2) {
      let thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
      for (let r = r1; r <= r2; r++) {
        let row = sheet.getRow(r);
        for (let c = c1; c <= c2; c++) {
          let cell = row.getCell(c);
          cell.border = { top: thin, left: thin, bottom: thin, right: thin };
          cell.alignment = Object.assign({ vertical: 'middle' }, cell.alignment || {});
        }
      }
    },
    async exportExcel() {
      let money = '#,##0';
      let headerFill = 'FFF2F2F2';
      let totalFill = 'FFFCF8E3';
      let workbook = new Excel.Workbook();

      // Sheet 1: Bảng hoa hồng phòng kinh doanh
      let sheet = workbook.addWorksheet(this.$t('sheetCommission'));
      sheet.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('fullName'), key: 'name', width: 22 },
        { header: this.$t('region'), key: 'office', width: 16 },
        { header: this.$t('position'), key: 'position', width: 16 },
        { header: this.$t('kpiPercent'), key: 'kpiPercent', width: 12 },
        { header: this.$t('kpiBracket'), key: 'kpiBracket', width: 12 },
        { header: this.$t('revenueBase'), key: 'revenueBase', width: 18, style: { numFmt: money } },
        { header: this.$t('commissionN'), key: 'commissionN', width: 18, style: { numFmt: money } },
        { header: this.$t('commissionNStar'), key: 'commissionNStar', width: 18, style: { numFmt: money } },
        { header: this.$t('commissionEAU'), key: 'commissionEAU', width: 20, style: { numFmt: money } },
        { header: this.$t('commissionCallFee'), key: 'commissionCallFee', width: 18, style: { numFmt: money } },
        { header: this.$t('total'), key: 'total', width: 18, style: { numFmt: money } },
      ];
      this.regions.forEach(region => {
        region.employees.forEach((item, index) => {
          sheet.addRow({
            stt: region.startIndex + index + 1,
            name: item.name,
            office: region.office,
            position: this.position(item.role),
            kpiPercent: this.percent(item.kpiPercent),
            kpiBracket: item.kpiBracket,
            revenueBase: item.revenueBase || 0,
            commissionN: item.commissionN || 0,
            commissionNStar: item.commissionNStar || 0,
            commissionEAU: item.commissionEAU || 0,
            commissionCallFee: item.commissionCallFee || 0,
            total: item.total || 0,
          });
        });
      });
      if (this.items.length) {
        let totalRow = sheet.addRow({
          name: this.$t('grandTotal'),
          revenueBase: this.mainTotal.revenueBase,
          commissionN: this.mainTotal.commissionN,
          commissionNStar: this.mainTotal.commissionNStar,
          commissionEAU: this.mainTotal.commissionEAU,
          commissionCallFee: this.mainTotal.commissionCallFee,
          total: this.mainTotal.total,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, totalFill, 12);
      }
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(sheet.getRow(1), headerFill, 12);
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      let mainLastRow = sheet.rowCount;
      this.xlsxBorderRange(sheet, 1, mainLastRow, 1, 12);

      // Bảng chính sách hoa hồng (đặt dưới bảng hoa hồng, cách 1 dòng trống)
      sheet.addRow([]);
      let policyTitleRow = sheet.addRow([this.$t('policyTitle')]);
      policyTitleRow.font = { bold: true, color: { argb: 'FFC00000' } };
      let policyHeaderRow = sheet.addRow([
        this.$t('kpi'),
        'CallioN (' + this.$t('policySourceSelf') + ')',
        'CallioN* (' + this.$t('policySourceCompany') + ')',
        'CallioE, CallioA, CallioU',
        this.$t('policyCallFee'),
        this.$t('policyBig'),
        this.$t('policyBigCallFee'),
      ]);
      policyHeaderRow.font = { bold: true };
      policyHeaderRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(policyHeaderRow, headerFill, 7);
      this.policy.forEach(row => {
        let r = sheet.addRow([row.kpi, row.n, row.nStar, row.eau, row.callFee, row.big, row.bigCallFee]);
        r.getCell(1).font = { bold: true };
      });
      this.xlsxBorderRange(sheet, policyHeaderRow.number, sheet.rowCount, 1, 7);

      // Sheet 2: Chi tiết đơn hàng được tính hoa hồng
      let detail = workbook.addWorksheet(this.$t('sheetDetail'));
      detail.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('dSales'), key: 'sales', width: 20 },
        { header: this.$t('dClientName'), key: 'companyName', width: 26 },
        { header: this.$t('dDomain'), key: 'domain', width: 22 },
        { header: this.$t('dSource'), key: 'source', width: 14 },
        { header: this.$t('dRevenueType'), key: 'loai', width: 14 },
        { header: this.$t('dPlanName'), key: 'planName', width: 12 },
        { header: this.$t('dUserPrice'), key: 'userPrice', width: 12, style: { numFmt: money } },
        { header: this.$t('dUserLimit'), key: 'userLimit', width: 12 },
        { header: this.$t('dExtraUser'), key: 'numberOfUserToBuy', width: 12 },
        { header: this.$t('dMonthCount'), key: 'monthCount', width: 10 },
        { header: this.$t('dConfirmTime'), key: 'confirmTime', width: 20 },
        { header: this.$t('dOrderValue'), key: 'orderValue', width: 18, style: { numFmt: money } },
        { header: this.$t('dAgencyPay'), key: 'agencyCommission', width: 16, style: { numFmt: money } },
        { header: this.$t('dRevenueBase'), key: 'revenueBase', width: 18, style: { numFmt: money } },
        { header: this.$t('dKpiBracket'), key: 'kpiBracket', width: 14 },
        { header: this.$t('dCommissionPercent'), key: 'commissionPercent', width: 14 },
        { header: this.$t('dCommission'), key: 'commission', width: 16, style: { numFmt: money } },
        { header: this.$t('dNote'), key: 'note', width: 32 },
      ];
      this.details.forEach((row, index) => {
        let r = detail.addRow({
          stt: index + 1,
          sales: row.sales,
          companyName: row.companyName,
          domain: row.domain,
          source: row.source,
          loai: row.loai,
          planName: row.planName,
          userPrice: row.userPrice || 0,
          userLimit: row.userLimit || 0,
          numberOfUserToBuy: row.numberOfUserToBuy || 0,
          monthCount: row.monthCount || 0,
          confirmTime: this.fmtTime(row.confirmTime),
          orderValue: row.orderValue || 0,
          agencyCommission: row.agencyCommission || 0,
          revenueBase: row.revenueBase || 0,
          kpiBracket: row.kpiBracket,
          commissionPercent: this.percent(row.commissionPercent),
          commission: row.commission || 0,
          note: row.note,
        });
        if (row.isReExtend) {
          this.xlsxFillRow(r, 'FFDFF0D8', 19);
        } else if (row.isBig) {
          this.xlsxFillRow(r, 'FFF2DEDE', 19);
        }
      });
      if (this.details.length) {
        let totalRow = detail.addRow({
          companyName: this.$t('grandTotal'),
          orderValue: this.detailTotal.orderValue,
          agencyCommission: this.detailTotal.agencyCommission,
          revenueBase: this.detailTotal.revenueBase,
          commission: this.detailTotal.commission,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, totalFill, 19);
      }
      detail.getRow(1).font = { bold: true };
      detail.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(detail.getRow(1), headerFill, 19);
      detail.views = [{ state: 'frozen', ySplit: 1 }];
      this.xlsxBorderRange(detail, 1, detail.rowCount, 1, 19);

      let buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `hoa-hong-${this.filterFormModel.period}.xlsx`);
    }
  },
  mounted() {
    this.load();
  },
  i18n: {
    messages: {
      en: {
        reportTitle: 'Sales commission month',
        selectSales: 'Select sales person',
        stt: 'No.',
        fullName: 'Full name',
        region: 'Department',
        position: 'Position',
        kpi: 'KPI',
        kpiPercent: 'KPI rate',
        kpiBracket: 'Bracket',
        revenueBase: 'Revenue for commission',
        commission: 'Commission',
        commissionN: 'Revenue commission CallioN',
        commissionNStar: 'Revenue commission CallioN*',
        commissionEAU: 'Revenue commission CallioE, CallioA, CallioU',
        commissionCallFee: 'Call fee commission',
        total: 'Total commission',
        roleConsultant: 'Sales',
        roleSupervisor: 'Supervisor',
        roleManager: 'Manager',
        unknownRegion: 'Unknown',
        noData: 'No KPI data for this month',
        policyTitle: 'Commission policy',
        policyCallFee: 'Call fee',
        policyBig: 'Customers with >15 users (over 12 months)',
        policyBigCallFee: 'Call fee of customers with >15 users (over 12 months)',
        policySourceSelf: 'Self-sourced',
        policySourceCompany: 'Company source',
        detailTitle: 'Commissioned orders detail month',
        dSales: 'Sales in charge',
        dClientName: 'Client name',
        dDomain: 'Domain',
        dSource: 'Client source',
        dRevenueType: 'Revenue type',
        dPlanName: 'Plan',
        dUserPrice: 'Price/user',
        dUserLimit: 'Max users',
        dExtraUser: 'Extra users bought',
        dMonthCount: 'Months',
        dConfirmTime: 'Confirm time',
        dOrderValue: 'Order value (after customer discount)',
        dAgencyPay: 'Agency payment',
        dRevenueBase: 'Revenue for sales commission',
        dKpiBracket: 'KPI bracket for commission',
        dCommissionPercent: 'Commission rate',
        dCommission: 'Commission received',
        dNote: 'Note',
        grandTotal: 'Grand total',
        exportExcel: 'Export Excel',
        sheetCommission: 'Commission',
        sheetDetail: 'Order detail',
        noDetail: 'No commissioned orders for this month',
      },
      vi: {
        reportTitle: 'Hoa hồng phòng kinh doanh tháng',
        selectSales: 'Chọn nhân viên kinh doanh',
        stt: 'STT',
        fullName: 'Họ tên',
        region: 'Phòng',
        position: 'Chức vụ',
        kpi: 'KPI',
        kpiPercent: 'Tỷ lệ đạt',
        kpiBracket: 'Mức',
        revenueBase: 'Doanh thu để tính hoa hồng cho sales',
        commission: 'Hoa hồng',
        commissionN: 'Hoa hồng doanh thu CallioN',
        commissionNStar: 'Hoa hồng doanh thu CallioN*',
        commissionEAU: 'Hoa hồng doanh thu CallioE, CallioA, CallioU',
        commissionCallFee: 'Hoa hồng tiền cước thoại Callio',
        total: 'Tổng cộng hoa hồng',
        roleConsultant: 'Kinh doanh',
        roleSupervisor: 'Trưởng nhóm',
        roleManager: 'Quản lý',
        unknownRegion: 'Chưa xác định',
        noData: 'Chưa có dữ liệu KPI cho tháng này',
        policyTitle: 'Chính sách hoa hồng',
        policyCallFee: 'Cước gọi',
        policyBig: 'Khách hàng có số user >15 (khách trên 12 tháng)',
        policyBigCallFee: 'Cước gọi khách hàng có số user >15 (khách trên 12 tháng)',
        policySourceSelf: 'Nguồn tự tìm',
        policySourceCompany: 'Nguồn công ty',
        detailTitle: 'Chi tiết đơn hàng tính hoa hồng tháng',
        dSales: 'Kinh doanh phụ trách',
        dClientName: 'Tên khách hàng',
        dDomain: 'Domain',
        dSource: 'Nguồn khách hàng',
        dRevenueType: 'Loại doanh thu',
        dPlanName: 'Tên gói',
        dUserPrice: 'Giá/user',
        dUserLimit: 'Số user tối đa',
        dExtraUser: 'Số user mua thêm',
        dMonthCount: 'Số tháng',
        dConfirmTime: 'Thời gian xác nhận',
        dOrderValue: 'Giá trị đơn hàng (sau khi trừ chi phí giảm giá cho KH)',
        dAgencyPay: 'Số tiền chi trả cho đại lý',
        dRevenueBase: 'Doanh thu để tính hoa hồng cho Sales',
        dKpiBracket: 'KPI tính hoa hồng',
        dCommissionPercent: '% Hoa hồng',
        dCommission: 'Hoa hồng được nhận',
        dNote: 'Ghi chú',
        grandTotal: 'Tổng cộng',
        exportExcel: 'Xuất dữ liệu',
        sheetCommission: 'Hoa hồng',
        sheetDetail: 'Chi tiết hoa hồng',
        noDetail: 'Chưa có đơn hàng được tính hoa hồng cho tháng này',
      }
    }
  }
}
</script>

<style scoped>
.c-template-table table.table thead th {
  background-color: #f2f2f2 !important;
}

.commission-detail-table th,
.commission-detail-table td {
  white-space: nowrap;
}
</style>
